<template>
  <div class="mt-4 w-100">
    <v-row>
      <v-col cols="12" md="3">
        <v-select
          :items="statusItems"
          item-text="text"
          item-value="value"
          v-model="localFilterItems.status"
          label="Claim Status"
          outlined
          clearable
          @change="onClaimStatusChange"
        />
      </v-col>
      <v-col cols="12" md="2">
        <v-text-field
          v-model="localFilterItems.claimId"
          placeholder="Claim ID"
          label="Claim ID"
          outlined
          clearable
          @input="onClaimIdChange"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="localFilterItems.submitter"
          label="Submitter's Name"
          persistent-hint
          outlined
          clearable
          @input="onSubmitterChange"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="localFilterItems.dealerName"
          label="Dealer Name"
          persistent-hint
          outlined
          clearable
          @input="onDealerNameChange"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="5">
        <v-row align="center">
          <v-col cols="5" md="5" class="pr-0">
            <v-text-field
              v-model="localFilterItems.claimsFromDate"
              label="Claims from Date"
              type="date"
              outlined
              clearable
              @input="onClaimsDateInput"
              @click:clear="onClearClaimsDate"
            />
          </v-col>
          <v-col cols="1" md="1" class="d-flex align-center justify-center pa-0 m-dash">
            &mdash;
          </v-col>
          <v-col cols="5" md="5" class="pa-0">
            <v-text-field
              v-model="localFilterItems.claimsToDate"
              label="Claims to Date"
              type="date"
              outlined
              clearable
              @input="onClaimsDateInput"
              @click:clear="onClearClaimsDate"
            />
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" md="5">
        <v-row align="center">
          <v-col cols="5" md="5" class="pr-0">
            <v-text-field
              v-model="localFilterItems.salesFromDate"
              label="Sales from Date"
              type="date"
              outlined
              clearable
              @input="onSalesDateInput"
              @click:clear="onClearSalesDate"
            />
          </v-col>
          <v-col cols="1" md="1" class="d-flex align-center justify-center pa-0 m-dash">
            &mdash;
          </v-col>
          <v-col cols="5" md="5" class="pa-0">
            <v-text-field
              v-model="localFilterItems.salesToDate"
              label="Sales to Date"
              type="date"
              outlined
              clearable
              @input="onSalesDateInput"
              @click:clear="onClearSalesDate"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { debounce } from "@/shared_data/functions";

export default {
  name: "AuditClaimsFilters",
  props: {
    value: {
      type: Object,
      required: true,
    },
    statusOptions: {
      type: Array,
      default: () => ["Pending Audit", "Needs More Info", "Approved", "Denied"],
    },
  },
  data() {
    return {
      localFilterItems: { ...this.value },
    };
  },
  computed: {
    statusItems() {
      return this.statusOptions.map((label) => ({
        text: label,
        value: this.mapLabelToStatusCode(label),
      }));
    },
  },
  watch: {
    value: {
      deep: true,
      handler(newVal) {
        this.localFilterItems = { ...newVal };
      },
    },
  },
  methods: {
    emitFilters(immediate = false) {
      this.$emit("input", { ...this.localFilterItems });
      this.$emit("filters-change", { ...this.localFilterItems, immediate });
    },

    mapLabelToStatusCode(label) {
      if (label === "Pending Audit") return "Pending";
      return label;
    },
    emitFiltersDebounced() {
      this._emitFiltersDebouncedFn && this._emitFiltersDebouncedFn();
    },

    onClaimStatusChange(value) {
      this.localFilterItems.status = value;
      this.emitFilters(true);
    },

    onClaimIdChange(value) {
      this.localFilterItems.claimId = value;
      if (value && value.length >= 3) {
        this.emitFiltersDebounced();
      } else if (!value) {
        this.emitFilters(true); // Immediate when empty
      } else {
        // 1-2 characters, no-op
      }
    },

    onSubmitterChange(value) {
      this.localFilterItems.submitter = value;
      if (value && value.length >= 3) {
        this.emitFiltersDebounced();
      } else if (!value) {
        this.emitFilters(true);
      } else {
        // 1-2 characters, no-op
      }
    },

    onDealerNameChange(value) {
      this.localFilterItems.dealerName = value;
      if (value && value.length >= 3) {
        this.emitFiltersDebounced();
      } else if (!value) {
        this.emitFilters(true);
      } else {
        // no-op
      }
    },

    onDateInput(dateType) {
      const fromDateKey = `${dateType}FromDate`;
      const toDateKey = `${dateType}ToDate`;
      this._dateInputDebouncedFn(() => {
        if (this.localFilterItems[fromDateKey] && this.localFilterItems[toDateKey]) {
          this.emitFilters(true);
        }
      });
    },

    onClaimsDateInput() {
      this.onDateInput("claims");
    },

    onSalesDateInput() {
      this.onDateInput("sales");
    },
    onClearDate(dateType) {
      console.log(`Clearing ${dateType} date filters`);
      const fromDateKey = `${dateType}FromDate`;
      const toDateKey = `${dateType}ToDate`;
      this.localFilterItems[fromDateKey] = null;
      this.localFilterItems[toDateKey] = null;
      this.emitFilters(true);
    },

    onClearClaimsDate() {
      this.onClearDate("claims");
    },

    onClearSalesDate() {
      this.onClearDate("sales");
    },
  },
  created() {
    this._emitFiltersDebouncedFn = debounce(() => {
      this.emitFilters(false);
    }, 500);

    // debounce for date inputs
    this._dateInputDebouncedFn = debounce((fn) => {
      fn();
    }, 1000);
  },
};
</script>

<style scoped>
.m-dash {
  text-align: center;
  font-size: 1.5em;
  margin-bottom: 1.2em;
}
</style>
